import {
	CAMPAIGN_CLASSIFICATION_AXIS_CODES,
	type CampaignClassificationCatalog,
	type CampaignClassificationCatalogSnapshot,
	type CampaignClassificationOption,
	type CampaignClassificationVariable,
} from '@/domain/campaign-classification-catalog'
import { CampaignError } from '@/domain/campaign-errors'
import { type AxisDTO, type AxisValueDTO, classificationApi } from '@/services/classification.service'
import { mapCampaignHttpError } from './campaign-http-error'

// HTTP-backed CampaignClassificationCatalog — one GET /axes, then six parallel
// GET /axes/{axisId}/values calls (never more, never repeated), bounded to the
// six approved axis codes. Never imports the fixture seed; the preserved
// in-memory campaign-classification-catalog-adapter.ts is untouched.

type RequiredAxisCode = (typeof CAMPAIGN_CLASSIFICATION_AXIS_CODES)[number]

async function fetchAxes(): Promise<AxisDTO[]> {
	try {
		return await classificationApi.listAxes()
	} catch (error) {
		throw mapCampaignHttpError(error)
	}
}

async function fetchAllValues(
	axisIdByCode: ReadonlyMap<RequiredAxisCode, string>,
): Promise<Map<string, AxisValueDTO[]>> {
	try {
		const entries = await Promise.all(
			[...axisIdByCode.entries()].map(
				async ([code, axisId]) => [code, await classificationApi.listValues(axisId)] as const,
			),
		)
		return new Map(entries)
	} catch (error) {
		throw mapCampaignHttpError(error)
	}
}

/** Resolves all six required axis ids via a full-array scan, before any value fetch is issued. */
function resolveRequiredAxisIds(axes: readonly AxisDTO[]): Map<RequiredAxisCode, string> {
	const result = new Map<RequiredAxisCode, string>()
	for (const code of CAMPAIGN_CLASSIFICATION_AXIS_CODES) {
		const matches = axes.filter((axis) => axis.code === code)
		if (matches.length > 1) {
			throw new CampaignError(
				'failure',
				`Campaign Classification catalog is unavailable: required axis "${code}" is ambiguous (multiple axes share this code).`,
			)
		}
		const axis = matches[0]
		if (!axis || axis.status !== 'active') {
			throw new CampaignError(
				'failure',
				`Campaign Classification catalog is unavailable: required axis "${code}" is missing or inactive.`,
			)
		}
		result.set(code, axis.axisId)
	}
	return result
}

function parseDate(value: string): Date | null {
	const parsed = new Date(value)
	return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** `status === 'active' && validFrom <= now && (validTo === null || validTo > now)`, unparseable dates never satisfy a bound. */
function isValueVisible(value: AxisValueDTO, now: Date): boolean {
	if (value.status !== 'active') return false
	const validFrom = parseDate(value.validFrom)
	if (validFrom === null || validFrom > now) return false
	if (value.validTo === null) return true
	const validTo = parseDate(value.validTo)
	return validTo !== null && validTo > now
}

/** Depth-first, `code ASC` tie-break at every level; anything the pass never reaches (a cycle, or an orphaned/invisible parent) is appended in `code ASC` order. Degenerates to plain `code ASC` when no option has a resolved `parentCode`. */
function orderOptions(options: readonly CampaignClassificationOption[]): CampaignClassificationOption[] {
	const byCodeAsc = (a: CampaignClassificationOption, b: CampaignClassificationOption) =>
		a.code < b.code ? -1 : a.code > b.code ? 1 : 0
	const byCode = new Map(options.map((option) => [option.code, option]))
	const childrenByParent = new Map<string, CampaignClassificationOption[]>()
	for (const option of options) {
		if (option.parentCode === undefined || option.parentCode === null || !byCode.has(option.parentCode)) continue
		const siblings = childrenByParent.get(option.parentCode) ?? []
		siblings.push(option)
		childrenByParent.set(option.parentCode, siblings)
	}
	for (const siblings of childrenByParent.values()) siblings.sort(byCodeAsc)

	const visited = new Set<string>()
	const ordered: CampaignClassificationOption[] = []
	const visit = (option: CampaignClassificationOption): void => {
		if (visited.has(option.code)) return
		visited.add(option.code)
		ordered.push(option)
		for (const child of childrenByParent.get(option.code) ?? []) visit(child)
	}
	const roots = options
		.filter((option) => option.parentCode === undefined || option.parentCode === null)
		.sort(byCodeAsc)
	for (const root of roots) visit(root)

	const unreached = options.filter((option) => !visited.has(option.code)).sort(byCodeAsc)
	return [...ordered, ...unreached]
}

/** Same-axis `parentId → parentCode`, resolved from the full pre-filter response; omitted when unresolvable. */
function transformAxisValues(rawValues: readonly AxisValueDTO[], now: Date): CampaignClassificationOption[] {
	const codeById = new Map(rawValues.map((value) => [value.valueId, value.code]))
	const visible = rawValues.filter((value) => isValueVisible(value, now))
	const options = visible.map((value): CampaignClassificationOption => {
		const parentCode = value.parentId !== null ? codeById.get(value.parentId) : undefined
		return parentCode === undefined
			? { code: value.code, label: value.label }
			: { code: value.code, label: value.label, parentCode }
	})
	return orderOptions(options)
}

function buildVariable(
	axisCode: RequiredAxisCode,
	axis: AxisDTO,
	rawValues: readonly AxisValueDTO[],
	now: Date,
): CampaignClassificationVariable {
	return { axisCode, label: axis.name, kind: axis.kind, options: transformAxisValues(rawValues, now) }
}

export const campaignClassificationCatalogHttpAdapter: CampaignClassificationCatalog = {
	async getCatalog(): Promise<CampaignClassificationCatalogSnapshot> {
		const axes = await fetchAxes()
		const axisIdByCode = resolveRequiredAxisIds(axes)
		const now = new Date()
		const valuesByCode = await fetchAllValues(axisIdByCode)
		const axisByCode = new Map(axes.map((axis) => [axis.code, axis]))

		const variableFor = (code: RequiredAxisCode): CampaignClassificationVariable =>
			buildVariable(code, axisByCode.get(code) as AxisDTO, valuesByCode.get(code) ?? [], now)

		const objective = variableFor('objective')
		const geography = variableFor('geography')
		const channel = variableFor('channel')
		const businessUnit = variableFor('business_unit')

		// TWO views of the ONE brand axis, split by `parentCode`: a brand is a root, a
		// product is anything beneath one. Both halves must be filtered — offering the whole
		// axis as "Brand" puts products in the Brand picker, and a product chosen there is
		// stored as the campaign's brand and then reads back into the Product field, so the
		// value appears to jump fields between save and reload.
		const wholeAxis = variableFor('brand')
		const brand: CampaignClassificationVariable = {
			...wholeAxis,
			options: wholeAxis.options.filter((option) => !option.parentCode),
		}
		const product: CampaignClassificationVariable = {
			axisCode: 'product',
			label: 'Product',
			kind: wholeAxis.kind,
			options: wholeAxis.options.filter((option) => Boolean(option.parentCode)),
		}

		return { objective, geography, brand, product, addable: [brand, channel, businessUnit, product] }
	},
}
